import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { MonitorDown, ShieldCheck, WifiOff, Server } from 'lucide-react';
import { posSetup, getApiError } from '../../api/client';
import { usePos } from '../../contexts/PosContext';
import Button from '../../components/common/Button';
import { colors, radii, shadows } from '../../theme';

/**
 * First-run screen on an installed till. Until the PC is bound to a branch it has no company, no
 * catalogue and no users, so this stands in front of the whole application — there is nothing
 * else it could usefully show.
 */
/**
 * The back office hands out two forms of the same thing: a 12-character code to type, and a longer
 * key that carries the server's address as well (`Helpers/PosActivationKey`). The key is what makes
 * this PC need no configuration — it works even when the installed package was built pointing
 * somewhere else — so the box accepts either. A '.' can only appear in a key, and a typed code is
 * never longer than 12 characters, which is enough to tell them apart while the operator types.
 */
const looksLikeKey = (value) =>
  value.includes('.') || value.replace(/[^0-9A-Za-z]/g, '').length > 12;

export default function Activate({ onActivated }) {
  const { status } = usePos();
  const [code, setCode] = useState('');
  // The address the till would use on its own: the one compiled into the package. Only interesting
  // when the code turns out not to carry one, which is exactly when this PC cannot reach anything.
  const packagedUrl = status?.cloudApiUrl ?? '';
  const [serverUrl, setServerUrl] = useState('');
  const [showServer, setShowServer] = useState(false);
  const [failedUrl, setFailedUrl] = useState(null);
  // Kept on screen rather than only in a toast: the server's reason for refusing ("this code is not
  // valid", "it has expired") is the whole instruction for what to do next, and a message that has
  // already faded leaves a screen that says nothing at all.
  const [error, setError] = useState(null);
  const isKey = looksLikeKey(code);

  const activate = useMutation({
    // Upper-casing a key would destroy it: base64url is case-sensitive.
    mutationFn: () => posSetup.activate(
      isKey ? code.trim() : code.trim().toUpperCase(),
      // Sent only when the operator opened the field and typed something; empty means "use the
      // address in the code, or the package's".
      showServer ? serverUrl.trim() : ''),
    onMutate: () => setError(null),
    onSuccess: (r) => {
      const branch = r.data.branchName;
      toast.success(branch
        ? `Arka u lidh me degën '${branch}'. Do të sinkronizohen vetëm të dhënat e kësaj dege.`
        : r.data.message || 'Pajisja u aktivizua.');
      onActivated?.();
    },
    onError: (e) => {
      // Sent only when the server never answered. Which one was tried is the missing half of "the
      // central server could not be reached", and the address box is the only way out of it — so
      // that failure opens it rather than leaving the operator to find it. A server that answered
      // and refused the code sends no address, and the box stays shut: the repair is a new code
      // from the back office, not an address typed here.
      const tried = e?.response?.data?.serverUrl;
      if (tried) {
        setFailedUrl(tried);
        setShowServer(true);
        if (!serverUrl) setServerUrl(tried);
      }
      const message = getApiError(e, 'Aktivizimi dështoi.');
      setError(message);
      toast.error(message);
    },
  });

  // Typed as XXXX-XXXX-XXXX so the code on screen matches the one in the back office. A pasted key
  // is left exactly as it is — the mask would strip the very characters it is made of.
  const handleChange = (e) => {
    const value = e.target.value;
    if (looksLikeKey(value)) {
      setCode(value.trim());
      return;
    }
    const raw = value.toUpperCase().replace(/[^0-9A-Z]/g, '').slice(0, 12);
    setCode(raw.match(/.{1,4}/g)?.join('-') ?? raw);
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: colors.bg, padding: 20,
    }}>
      <div style={{
        width: '100%', maxWidth: 460, background: colors.surface, borderRadius: radii.xl,
        border: `1px solid ${colors.border}`, boxShadow: shadows.lg, padding: 32,
      }}>
        <div style={{
          width: 52, height: 52, borderRadius: radii.md, background: colors.gradient,
          display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 18,
        }}>
          <MonitorDown size={26} color="#fff" />
        </div>

        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', color: colors.text }}>
          Aktivizo këtë arkë
        </h1>
        <p style={{ margin: '8px 0 22px', fontSize: 14, color: colors.textMuted, lineHeight: 1.7 }}>
          Kjo pajisje ende nuk është lidhur me një degë. Hap StockFlow në zyrën qendrore,
          shko te <strong>Cilësimet → Arkat e instaluara</strong>, shto një arkë <strong>duke
          zgjedhur degën</strong> ku do të punojë kjo PC, dhe shkruaj këtu kodin që shfaqet — ose
          ngjit kodin e plotë me butonin <em>Kopjo</em>. Kodi e lidh arkën me atë degë: pastaj
          sinkronizohen vetëm të dhënat e saj, jo i tërë katalogu i kompanisë.
        </p>

        <form onSubmit={(e) => { e.preventDefault(); activate.mutate(); }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: colors.text }}>Kodi i aktivizimit</label>
          <input
            value={code}
            onChange={handleChange}
            placeholder="XXXX-XXXX-XXXX"
            autoFocus
            style={{
              width: '100%', boxSizing: 'border-box', marginTop: 6, marginBottom: 18,
              padding: '14px 16px', fontFamily: 'monospace',
              fontSize: isKey ? 12 : 20,
              letterSpacing: isKey ? 0 : '0.14em',
              textAlign: isKey ? 'left' : 'center',
              border: `1px solid ${colors.borderStrong}`,
              borderRadius: radii.md, outline: 'none', color: colors.text,
            }}
          />
          {/* The last resort. A key carries its server's address and needs none of this; a plain
              code falls back to the address baked into the installer, and when that one is wrong
              the PC has no other way out short of editing a file under Program Files. */}
          {!showServer ? (
            <button type="button" onClick={() => { setShowServer(true); setServerUrl(packagedUrl); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 7, margin: '-8px 0 16px', padding: 0,
                border: 'none', background: 'none', cursor: 'pointer',
                fontSize: 12.5, color: colors.textMuted,
              }}>
              <Server size={14} /> Shkruaj adresën e serverit
            </button>
          ) : (
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: colors.text }}>Adresa e serverit</label>
              <input
                value={serverUrl}
                onChange={(e) => setServerUrl(e.target.value)}
                placeholder="http://192.168.1.5:5000/api"
                style={{
                  width: '100%', boxSizing: 'border-box', marginTop: 6, padding: '11px 14px',
                  fontFamily: 'monospace', fontSize: 13,
                  border: `1px solid ${failedUrl ? colors.danger : colors.borderStrong}`,
                  borderRadius: radii.md, outline: 'none', color: colors.text,
                }}
              />
              <div style={{ marginTop: 7, fontSize: 12, color: colors.textMuted, lineHeight: 1.7 }}>
                {failedUrl
                  ? <>Nuk u arrit <code>{failedUrl}</code>. Shkruaj këtu adresën ku hapet StockFlow
                      në zyrën qendrore (p.sh. <code>http://192.168.1.5:5000</code>) — ose kthehu atje,
                      lësho kodin sërish dhe ngjit <strong>kodin e plotë</strong>, që e bart vetë adresën.</>
                  : <>Lëre bosh nëse ke ngjitur kodin e plotë — ai e bart vetë adresën. Përdore këtë
                      vetëm kur ke shkruar kodin e shkurtër dhe serveri nuk gjendet.</>}
              </div>
            </div>
          )}

          {error && (
            <div style={{
              marginBottom: 16, padding: '11px 13px', borderRadius: radii.md,
              background: `${colors.danger}14`, border: `1px solid ${colors.danger}40`,
              fontSize: 13, lineHeight: 1.65, color: colors.danger,
            }}>
              {error}
            </div>
          )}

          <Button type="submit" size="lg" loading={activate.isPending}
            disabled={isKey ? code.trim().length < 12 : code.replace(/-/g, '').length !== 12}
            style={{ width: '100%' }}>
            <ShieldCheck size={18} /> Aktivizo
          </Button>
        </form>

        <div style={{
          marginTop: 22, padding: 13, background: colors.surfaceMuted, borderRadius: radii.md,
          fontSize: 12.5, color: colors.textMuted, lineHeight: 1.8, display: 'flex', gap: 10,
        }}>
          <WifiOff size={16} style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            Aktivizimi kryhet një herë të vetme dhe kërkon internet. Pas tij arka punon edhe
            pa lidhje: shitjet, kuponët fiskalë dhe stoku ruhen lokalisht dhe dërgohen
            automatikisht sapo lidhja të kthehet.
          </div>
        </div>
      </div>
    </div>
  );
}
